import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, MailCheck, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TAGLINE, SUB_TAGLINE } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PasswordStrength, passwordScore } from "@/components/PasswordStrength";
import PublicHeader from "@/components/PublicHeader";
import { useMeta } from "@/lib/useMeta";

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden>
    <path fill="#4285F4" d="M21.6 12.2c0-.8-.07-1.35-.2-1.95H12v3.7h5.5c-.11.9-.71 2.26-2 3.17l-.02.12 2.9 2.25.2.02c1.85-1.7 2.92-4.2 2.92-7.31z" />
    <path fill="#34A853" d="M12 22c2.7 0 4.96-.89 6.6-2.42l-3.14-2.44c-.84.59-1.97 1-3.46 1a5.36 5.36 0 0 1-5.07-3.7l-.11.01-2.98 2.31-.04.11A10 10 0 0 0 12 22z" />
    <path fill="#FBBC05" d="M6.93 14.44A6.6 6.6 0 0 1 6.57 12c0-.85.15-1.67.35-2.44l-.01-.16-3.02-2.35-.1.05A10 10 0 0 0 2.7 12c0 1.61.39 3.13 1.09 4.46l3.14-2.02z" />
    <path fill="#EA4335" d="M12 5.86c1.88 0 3.15.81 3.87 1.49l2.83-2.76C16.95 2.99 14.7 2 12 2 8.09 2 4.71 4.24 3.09 7.5l3.13 2.44A5.38 5.38 0 0 1 12 5.86z" />
  </svg>
);

const SIZES = ["1-10", "11-50", "51-200", "200+"];
const FIELDS = [
  ["full_name", "Full name", "text"],
  ["company_name", "Company name", "text"],
  ["designation", "Designation", "text"],
  ["mobile", "Mobile number", "tel"],
  ["email", "Email", "email"],
];

export default function Signup() {
  useMeta({
    title: "Start Your Free 14-Day Trial | Plan-Sarthi EPC Project Software",
    description:
      "Create your Plan-Sarthi account and start a 14-day free trial — no credit card required. Any email works: personal or work.",
    path: "/signup",
  });
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [form, setForm] = useState({
    full_name: "", company_name: "", designation: "", mobile: "", email: "", password: "",
    company_size: "11-50",
  });
  const [stage, setStage] = useState("form");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [busy, setBusy] = useState(false);
  const score = useMemo(() => passwordScore(form.password), [form.password]);

  useEffect(() => {
    if (stage === "pending") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [stage]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignup = () => {
    const redirectUrl = window.location.origin + "/projects";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (score.met < 4) return toast.error("Please choose a password that meets all four rules");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", form);
      setStage("pending");
      if (data.dev_otp) setDevOtp(data.dev_otp);
      toast.success("Account created — check your email to verify it");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email: form.email, otp });
      localStorage.setItem("cx_token", data.access_token);
      localStorage.setItem("cx_user", JSON.stringify(data));
      setSession(data);
      toast.success("Email verified — your 14-day trial has started");
      navigate("/projects");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification", { email: form.email });
      if (data.dev_otp) setDevOtp(data.dev_otp);
      toast.success("New verification email sent");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
    <PublicHeader />
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-[1fr_1.05fr]" data-testid="signup-page">
      <div className="relative hidden lg:block border-r border-border overflow-hidden">
        <img src="https://images.pexels.com/photos/15532135/pexels-photo-15532135.jpeg" alt="Industrial EPC site" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(115deg,rgba(4,14,26,.97),rgba(11,32,56,.85))" }} />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div />
          <div>
            <span className="inline-block border border-[#F97316]/50 text-[#F97316] text-[10px] uppercase tracking-[0.18em] px-2 py-1">{SUB_TAGLINE}</span>
            <h1 className="font-display text-3xl xl:text-4xl font-semibold leading-[1.12] mt-4">
              Start your <span className="text-[#F97316]">14-day free trial</span>
            </h1>
            <ul className="mt-6 space-y-2.5 text-[13px] text-white/75">
              {["Any email works — personal or work, no domain restrictions",
                "WBS, DPR, CPM, EVM costing, store and workforce KPIs",
                "No card required to start — upgrade when you're ready",
                "AI modules (Risk Radar, BOQ Parser) unlock on a paid plan"].map((t) => (
                <li key={t} className="flex gap-2"><ShieldCheck className="w-4 h-4 text-[#F97316] shrink-0 mt-0.5" />{t}</li>
              ))}
            </ul>
          </div>
          <div className="text-[11px] text-white/40 uppercase tracking-[0.18em]">{TAGLINE}</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {stage === "form" ? (
            <>
              <h2 className="text-2xl font-semibold">Create your workspace</h2>
              <p className="text-sm text-muted-foreground mt-1">14 days free. No card required.</p>
              <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="signup-form">
                {FIELDS.map(([k, l, type]) => (
                  <div key={k} className={`space-y-1.5 ${k === "email" ? "sm:col-span-2" : ""}`}>
                    <Label className="text-xs uppercase tracking-wider">{l}</Label>
                    <Input
                      type={type}
                      required={k !== "designation"}
                      value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      data-testid={`signup-${k}-input`}
                    />
                  </div>
                ))}
                <div className="sm:col-span-2 space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">Password</Label>
                  <Input
                    type="password"
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="signup-password-input"
                  />
                  <PasswordStrength password={form.password} />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">Company size</Label>
                  <Select value={form.company_size} onValueChange={(v) => setForm({ ...form, company_size: v })}>
                    <SelectTrigger data-testid="signup-company_size-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s} employees</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="sm:col-span-2" disabled={busy} data-testid="signup-submit-btn">
                  {busy ? "Creating account…" : "Start free trial"} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </form>

              <div className="flex items-center gap-3 my-5">
                <span className="h-px flex-1 bg-border" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">or</span>
                <span className="h-px flex-1 bg-border" />
              </div>
              <Button type="button" variant="outline" className="w-full gap-2" onClick={googleSignup} data-testid="google-login-btn">
                <GoogleMark /> Continue with Google
              </Button>

              <p className="text-[12px] text-muted-foreground mt-5">
                Already have an account? <Link to="/login" className="text-primary hover:underline" data-testid="signup-login-link">Sign in</Link>
              </p>
            </>
          ) : (
            <div data-testid="pending-verification-screen">
              <MailCheck className="w-8 h-8 text-[#F97316]" />
              <h2 className="text-2xl font-semibold mt-3">Verify your email</h2>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                Check your email to verify your account before logging in. We sent a verification link and a 6-digit code
                to <strong className="text-foreground">{form.email}</strong>. No part of the workspace opens until this is
                confirmed.
              </p>
              {devOtp ? (
                <div className="border border-[#F97316]/40 bg-[#F97316]/10 p-2.5 mt-4 text-[12px]" data-testid="dev-otp-notice">
                  Preview mode (SendGrid key not set): your code is{" "}
                  <strong className="num text-base tracking-widest">{devOtp}</strong>
                </div>
              ) : null}
              <form onSubmit={verify} className="mt-5 space-y-3" data-testid="verify-form">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">Verification code</Label>
                  <Input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric"
                    placeholder="000000"
                    className="num text-lg tracking-[0.4em]"
                    data-testid="verify-otp-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={busy || otp.length !== 6} data-testid="verify-submit-btn">
                  {busy ? "Verifying…" : "Verify & start trial"}
                </Button>
              </form>
              <div className="flex items-center justify-between mt-4 text-[12px]">
                <button onClick={resend} className="text-primary hover:underline" data-testid="resend-otp-btn">Resend email</button>
                <button onClick={() => setStage("form")} className="text-muted-foreground hover:text-foreground" data-testid="back-to-form-btn">Change details</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}
